import type {ReactElement} from 'react';
import {v4 as uuidv4} from 'uuid';
import {useAppDispatch, useAppSelector} from '@/hooks';
import type {AiVideosState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import {AiVideoGenerationStatus} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import {addAiVideoGenerationItem, removeAiVideoGenerationItem, selectFailedAiVideos} from '@Components/poster-editor/poster-editor-reducer';
import {Icon} from '@Components/icon-v2';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {getAiVideoGenerationErrorMessage} from '@Libraries/ai-video.library';
import {AI_VIDEO_LOADING_SKELETON_ID_PREFIX} from '@Panels/ai-video-panel/components/generated-video-grid/generated-video-grid.types';

export function AiVideoFailedGenerationsList(): ReactElement | null {
  const dispatch = useAppDispatch();
  const failedVideos = useAppSelector(selectFailedAiVideos);

  if (failedVideos.length === 0) {
    return null;
  }

  const onRetry = (item: AiVideosState): void => {
    dispatch(removeAiVideoGenerationItem(item.id));
    dispatch(
      addAiVideoGenerationItem({
        id: `${AI_VIDEO_LOADING_SKELETON_ID_PREFIX}${uuidv4()}`,
        status: AiVideoGenerationStatus.PENDING,
        params: item.params,
      })
    );
  };

  const onDismiss = (item: AiVideosState): void => {
    dispatch(removeAiVideoGenerationItem(item.id));
  };

  const renderRetryLink = (item: AiVideosState): ReactElement | null => {
    if (item.errorCode) {
      return null;
    }
    return (
      <Text
        className={`_cursor-pointer ct-error-primary`}
        val={window.i18next.t('pmwjs_retry')}
        size={TextSize.XSMALL}
        bold
        underline
        onClick={(): void => {
          onRetry(item);
        }}
      />
    );
  };

  const renderErrorMessage = (item: AiVideosState): ReactElement => {
    return (
      <div key={item.id} className={`flexbox flex-align-items-center gap-2 radius-8 spacing-p-2 bg-danger-10`}>
        <Icon className={'ct-error-primary'} icon="icon-exclamation-triangle" size={IconSize.SIZE_ICON_16} />
        <div className="flexbox flex-align-items-center flex-1 gap-1">
          <Text className={'ct-error-primary'} val={getAiVideoGenerationErrorMessage(item.errorCode)} size={TextSize.XSMALL} />
          {renderRetryLink(item)}
        </div>
        <Icon
          className={'ct-neutral-primary _cursor-pointer'}
          icon={'icon-close'}
          size={IconSize.SIZE_ICON_20}
          onClick={(): void => {
            onDismiss(item);
          }}
          type={IconType.SECONDARY}
        />
      </div>
    );
  };

  return <div className={`spacing-p-b-3 flex-v-row gap-2 _full-width`}>{failedVideos.map(renderErrorMessage)}</div>;
}
